import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apps } from '../utils/dataModel';
import { trackEvent } from '../utils/analytics';
import Modal from './Modal';
import ContactForm from './ContactForm';
import SEO from './SEO';

const principles = [
  {
    title: 'Bought once, not rented.',
    body: 'Software is getting cheaper to build. Subscriptions made sense when a product needed a big team to keep it running. For a focused tool, a one-time price is the honest one.'
  },
  {
    title: 'Your data lives on your device.',
    body: 'No account to create and no servers of mine holding your information. If an app needs an outside service, like Meet using Claude for AI, it connects with your own account, straight to that provider.'
  },
  {
    title: 'Focused, not bloated.',
    body: "I'm one developer with no sales team and no growth roadmap to feed. That's how the price stays low: each app solves its core problem well and skips the extras that add engineers and cost."
  }
];

const AppsPage = () => {
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCtaClick = (app, ctaLabel) => {
    trackEvent('app_cta_click', {
      app_id: app.id,
      app_name: app.name,
      cta: ctaLabel
    });
  };

  return (
    <>
      <SEO
        title="Apps I've Built | Alek Hagopian"
        description="Software I build and maintain myself: Solidly Finance, a private offline financial planner, and Solidly Meet, a desktop app that brings Claude into your meetings live. Both are one-time purchases."
      />

      <div className="downloads-page">
        <div className="downloads-container">
          <div className="project-breadcrumb">
            <Link to="/">Home</Link>
            <span> / </span>
            <span>Apps</span>
          </div>

          <div className="downloads-header">
            <h1>Apps I've Built</h1>
            <p>
              Consulting is most of what I do, but not all of it. These are products I designed,
              built, and still maintain — the same problems I solve for clients, aimed at my own
              itches instead.
            </p>
          </div>

          <div className="app-principles">
            {principles.map((principle) => (
              <div key={principle.title} className="app-principle">
                <h2>{principle.title}</h2>
                <p>{principle.body}</p>
              </div>
            ))}
          </div>

          <div className="downloads-content">
            <div className="downloads-grid">
              {apps.map((app) => (
                <div key={app.id} className="download-card">
                  <div className="download-badge">
                    {app.platforms.join(' · ')}
                  </div>

                  <div className="app-preview-downloads">
                    <img
                      src={app.logo}
                      alt={`${app.name} logo`}
                      className="app-screenshot-downloads"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>

                  <h2>{app.name}</h2>
                  <p className="download-size">{app.tagline}</p>
                  <p>{app.description}</p>

                  <ul className="details-list">
                    {app.highlights.map((highlight, index) => (
                      <li key={index} className="detail-item">
                        <span className="detail-icon">✓</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>

                  {app.requirements && (
                    <p className="app-requirements">
                      <span className="app-requirements-label">What you need:</span>{' '}
                      {app.requirements}
                    </p>
                  )}

                  <div className="cta-buttons">
                    <a
                      href={app.cta.url}
                      className="download-btn primary"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleCtaClick(app, app.cta.label)}
                    >
                      {app.cta.label}
                      {' →'}
                    </a>
                    {app.secondaryCta && (
                      <a
                        href={app.secondaryCta.url}
                        className="download-btn secondary"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleCtaClick(app, app.secondaryCta.label)}
                      >
                        {app.secondaryCta.label}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="download-help-section">
            <h2>Want something like this for your company?</h2>
            <p>
              These started the same way client projects do — a problem worth solving and a
              prototype to find out if it was tractable.
            </p>
            <div className="cta-buttons">
              <button className="btn btn-primary" onClick={() => setShowContactModal(true)}>
                Work with me →
              </button>
              <Link to="/work" className="btn btn-secondary">See my client work</Link>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Start a conversation"
      >
        <ContactForm onClose={() => setShowContactModal(false)} />
      </Modal>
    </>
  );
};

export default AppsPage;
